import { Component, ChangeDetectionStrategy, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AuthStore } from '@core/services/auth/auth.store';
import { LegalConsentService } from '@core/services/data/legal-consent.service';
import { ReconsentDialogComponent } from '../../shared/components/legal-consent/reconsent-dialog.component';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RouterOutlet } from '@angular/router';
import { VexConfigService } from '@vex/config/vex-config.service';

import { AsyncPipe } from '@angular/common';
import { SidenavComponent } from '../components/sidenav/sidenav.component';
import { ToolbarComponent } from '../components/toolbar/toolbar.component';
import { QuickpanelComponent } from '../components/quickpanel/quickpanel.component';
import { BaseLayoutComponent } from '../base-layout/base-layout.component';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { SearchComponent } from '../components/toolbar/search/search.component';
import { VexProgressBarComponent } from '@vex/components/vex-progress-bar/vex-progress-bar.component';
import { VexConfig } from '@vex/config/vex-config.interface';
// Contextual navigation is now integrated into the main sidenav
import { FcmService } from '@core/services/infra/fcm.service';
import { DesktopNotificationsService } from '@core/services/infra/desktop-notifications.service';
import { ProgressiveLoadingCoordinatorService } from '@core/services/infra/progressive-loading-coordinator.service';
import { AuthenticatedLoadingCoordinatorService } from '@core/services/infra/authenticated-loading-coordinator.service';
import { AuthService } from '@core/services/auth/auth.service';
import { UsageLimitBannerComponent } from '../../shared/components/usage-limit-banner/usage-limit-banner.component';
import { VexSecondaryToolbarComponent } from '@vex/components/vex-secondary-toolbar/vex-secondary-toolbar.component';
import { SecondaryToolbarPresenceService } from '@core/services/navigation/secondary-toolbar-presence.service';

@Component({
    selector: 'vex-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    BaseLayoutComponent,
    AsyncPipe,
    SidenavComponent,
    ToolbarComponent,
    QuickpanelComponent,
    MatSidenavModule,
    RouterOutlet,
    SearchComponent,
    VexProgressBarComponent,
    UsageLimitBannerComponent,
    VexSecondaryToolbarComponent
]
})
export class LayoutComponent {
  protected readonly toolbarPresence = inject(SecondaryToolbarPresenceService);
  config$: Observable<VexConfig> = this.configService.config$;
  sidenavDisableClose$: Observable<boolean> = this.layoutService.isDesktop$;
  sidenavFixedInViewport$: Observable<boolean> =
    this.layoutService.isDesktop$.pipe(map((isDesktop) => !isDesktop));
  sidenavMode$: Observable<MatDrawerMode> = combineLatest([
    this.layoutService.gtMd$, // Use gtMd$ (≥960px) instead of isDesktop$ (≥1280px)
    this.configService.select((config) => config.layout)
  ]).pipe(
    map(([isGtMd, layout]) =>
      !isGtMd || layout === 'vertical' ? 'over' : 'side'
    )
  );
  // In overlay ('over') mode the sidenav is a full drawer with a backdrop, so the
  // collapsed icon-rail (only useful as a thin side strip on desktop) must never
  // apply — otherwise tapping the burger on mobile reveals an unreadable icon rail.
  sidenavCollapsed$: Observable<boolean> = combineLatest([
    this.layoutService.sidenavCollapsed$,
    this.sidenavMode$
  ]).pipe(map(([collapsed, mode]) => (mode === 'over' ? false : collapsed)));
  // Posé sur l'hôte `<mat-sidenav>` et non sur `.vex-sidenav` : l'ombre de
  // superposition serait rognée par l'`overflow: auto` de `.mat-drawer` et de
  // `.mat-drawer-inner-container`.
  sidenavRailHoverOpen$: Observable<boolean> =
    this.layoutService.sidenavRailHoverOpen$;
  sidenavOpen$: Observable<boolean> = this.layoutService.sidenavOpen$;
  quickpanelOpen$: Observable<boolean> = this.layoutService.quickpanelOpen$;

  // Contextual navigation is now integrated into the main sidenav

  // Email banner visibility
  showEmailBanner$: Observable<boolean>;

  // Main layout visibility control
  shouldShowMainLayout$: Observable<boolean>;

  // Scrollbar detection is no longer needed with unified sidenav

  private readonly authStore = inject(AuthStore);
  private readonly legalConsent = inject(LegalConsentService);
  private readonly dialog = inject(MatDialog);
  private readonly fcm = inject(FcmService);
  private readonly desktopNotifications = inject(DesktopNotificationsService);
  /** Guards against re-opening the blocking re-consent modal while it's up. */
  private reconsentOpen = false;
  /** So a re-render of the layout never registers the same token twice. */
  private pushBootstrapped = false;

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly configService: VexConfigService,
    public readonly coordinator: ProgressiveLoadingCoordinatorService,
    private readonly authService: AuthService,
    public readonly authenticatedLoadingCoordinator: AuthenticatedLoadingCoordinatorService,
    // ElementRef and Renderer2 no longer needed
  ) {
    // Loading coordination is now handled by AuthenticatedLoadingCoordinatorService

    // Subscribe to email verification banner state - show if authenticated, new user, and email not verified
    this.showEmailBanner$ = combineLatest([
      toObservable(this.authStore.isAuthenticated),
      this.authService.isFirstTimeUser(),
      this.authService.getEmailVerificationStatus()
    ]).pipe(
      map(([isAuthenticated, isNewUser, isEmailVerified]) => {
        const shouldShow = isAuthenticated && isNewUser && !isEmailVerified;
        console.log("🔔 Layout: Email banner should show:", shouldShow, "Auth:", isAuthenticated, "New:", isNewUser, "Verified:", isEmailVerified);
        return shouldShow;
      })
    );

    // Set up main layout visibility control
    this.shouldShowMainLayout$ = combineLatest([
      this.config$,
      this.authenticatedLoadingCoordinator.shouldShowLoadingScreen$
    ]).pipe(
      map(([config, shouldShowAuthLoading]) => {
        return !!config && !shouldShowAuthLoading;
      })
    );

    // Onboarding is now handled by the OnboardingService and individual page components
    // No need to check for onboarding here to avoid duplicate popups

    // Blocking re-consent gate. Once the user document is hydrated, if the
    // recorded legal consent is missing or stale (a document version was
    // bumped), open the disableClose modal so the user must re-accept (or
    // sign out) before using the app. Covers existing accounts created before
    // this feature and any future version change. Freshly-onboarded users
    // already consented, so this never double-prompts them.
    effect(() => {
      if (this.legalConsent.needsConsent() && !this.reconsentOpen) {
        this.reconsentOpen = true;
        this.dialog
          .open(ReconsentDialogComponent, {
            disableClose: true,
            width: '600px',
            maxWidth: '94vw',
            autoFocus: false,
          })
          .afterClosed()
          .subscribe(() => {
            this.reconsentOpen = false;
          });
      }
    });

    // Push bootstrap for MANAGERS. Until #285 only the tenant shell did this,
    // so a manager could tick "desktop alerts" and never own a token — the
    // preference held, the notification never came.
    //
    // This registers WITHOUT prompting: `registerForUser` returns
    // `permission-default` when the browser has not been asked, and the ask
    // belongs to the settings switch or the opt-in banner. The single
    // irreversible prompt this app gets must not be spent by a page load.
    effect(() => {
      const userId = this.authStore.user()?.userUID;
      if (!userId || this.pushBootstrapped) return;
      this.pushBootstrapped = true;
      void this.fcm.registerForUser(userId).then(() => this.desktopNotifications.start());
    });
  }

  onSidenavClosed(): void {
    this.layoutService.closeSidenav();
  }

  onQuickpanelClosed(): void {
    this.layoutService.closeQuickpanel();
  }

  // Scrollbar detection methods removed - no longer needed with unified sidenav

}
